import _ from "lodash";
import { connect } from "react-redux";
import moment from "moment";
import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import {
  allottedTimeFactory,
  PRESCRIBE,
} from "@/components/Provider/ScheduleAvailability/constants";
import { awaitNotesSummaryRefresh } from "@/features/notes/(v2)/helpers/awaitNotesSummaryRefresh";
import {
  buildInterventionAndPlanFormFromGoalAndObjectives,
  checkInterventionAndPlanErrors,
} from "@/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/InterventionAndPlan/InterventionAndPlanV2/InterventionAndPlanV2FormSchema";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { createEmptyNote } from "@/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/WorkflowFunctions";
import { fetchNecessaryNotesByWorkflow } from "@/components/Common/GuidedVideoCall/Steps/ProviderVideoWorkflowStep";
import { formatAllottedTime } from "@/features/notes/helpers/format-time";
import { getCompletedCalls } from "@/features/notes/helpers/completed-calls";
import { getCPTWorkflow } from "@/components/Common/GuidedVideoCall/Steps/ProviderWorkflow/ProviderWorkflows";
import { GUIDED_SESSION_CPT_OPTIONS } from "@/features/notes/(v2)/(guided-session)/constants";
import { GuidedSessionView } from "@/features/notes/(v2)/(guided-session)/GuidedSessionView";
import { providerRequests } from "@/api/(requests)/provider";
import { providerTypeToDefaultNoteEventType } from "@/features/notes/helpers/provider-to-note-event";
import { sharedRequests } from "@/api/(requests)/shared";
import { updateNote } from "@/features/notes/(v2)/helpers/update-note";

import { EditNoteWrapper } from "./components/EditNoteWrapper";
import { NotesLinkCall } from "./components/NotesLinkCall";

class NewNoteV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      event_type: null,
      lastNote: null,
      note: null,
      noteErrors: {},
      patient: null,
      patientDiagnosis: null,
      patientId: props.match.params.patientId,
      workflow: null,
    };
  }

  getCPTOptions = () => {
    return this.props.profile.provider_type
      ? GUIDED_SESSION_CPT_OPTIONS.filter((option) =>
          option.provider_types.includes(this.props.profile.provider_type),
        )
      : [];
  };

  componentDidMount = async () => {
    try {
      let urlParams = { patientId: this.state.patientId };
      let patient = (await sharedRequests.fetch_patient_details({ urlParams })).user;
      const patientDiagnosis = await sharedRequests.get_patient_diagnosis({ urlParams });
      const event_type = providerTypeToDefaultNoteEventType(this.props.profile);
      const data = {
        event_type: event_type,
      };

      const questionSet = await sharedRequests.get_patient_call_question_sets({ data });
      const workflow = _.cloneDeep(getCPTWorkflow(event_type, this.props.profile));

      const validNoteTypes = [
        "Video Consultation",
        "Check-in",
        "Care Coordination",
        "Initial Clinical Assessment",
        "Care Communication",
        "Case Management",
        null,
      ];

      const lastCompletedNoteByProviderIdQueryParams = {
        completed_date_time_exists: true,
        is_not_patient_no_show: true,
        limit_one_per_provider_type: true,
        note_state_is_not: "draft",
        note_types: validNoteTypes.join(","),
        provider_id: this.props.profile.username,
        version: workflow.details.noteVersion,
      };

      let lastNoteCompletedByCurrentProviderResponse =
        await sharedRequests.fetch_patient_progress_notes({
          searchParams: lastCompletedNoteByProviderIdQueryParams,
          urlParams,
        });

      const lastNote = lastNoteCompletedByCurrentProviderResponse
        ? lastNoteCompletedByCurrentProviderResponse[0]
        : null;

      const lastCompletedNotesByOtherProvidersQueryParams = {
        completed_date_time_exists: true,
        is_not_patient_no_show: true,
        limit_one_per_provider_type: true,
        note_state_is_not: "draft",
        note_types: validNoteTypes.join(","),
        provider_type_begins_with: workflow.details.viewLastNoteOther
          ? workflow.details.viewLastNoteOther
          : null,
        version: workflow.details.noteVersion,
      };

      let lastCompletedNotesByOtherProvidersResponse =
        await sharedRequests.fetch_patient_progress_notes({
          searchParams: lastCompletedNotesByOtherProvidersQueryParams,
          urlParams,
        });

      const lastNotesOther = fetchNecessaryNotesByWorkflow(
        workflow.details,
        lastCompletedNotesByOtherProvidersResponse,
      );

      const cptOptions = this.getCPTOptions();
      const completedCalls = await getCompletedCalls(urlParams, (call) =>
        cptOptions.map((option) => option.value).includes(call.event_type),
      );

      let goalAndObjectiveForm = null;

      if (this.props.profile.provider_type === "BEHAVIORAL") {
        const goalsAndObjectives = await carePlanRequests.get_goals_and_objectives({
          urlParams,
        });

        goalAndObjectiveForm = buildInterventionAndPlanFormFromGoalAndObjectives(
          goalsAndObjectives,
          this.props.profile.provider_type,
        );
      }

      const note = this.getEmptyNote(
        patient,
        lastNote,
        lastNotesOther,
        patientDiagnosis,
        event_type,
        this.props.profile,
        questionSet,
        workflow,
        goalAndObjectiveForm,
      );

      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        completedCalls,
        event_type,
        lastNote,
        lastNotesOther,
        note,
        patient,
        patientDiagnosis,
        questionSet,
        workflow,
      });
    } catch (e) {
      console.log(e);
    }
  };

  getEmptyNote = (
    patient,
    lastNote,
    lastNotesOther,
    patientDiagnosis,
    eventType,
    provider,
    questionSet,
    workflow,
    goalAndObjectiveForm,
  ) => {
    const workflowDetails = workflow.details;
    const allottedTime = allottedTimeFactory(eventType);
    const callDetails = {
      allotted_time: allottedTime,
      callId: "",
      event_type: eventType,
      provider_role: provider.provider_type,
      timestamp: moment().utc().format("YYYY-MM-DD HH:mm"),
    };

    let note = createEmptyNote(callDetails, workflowDetails.noteVersion, patientDiagnosis);

    if (workflowDetails.noteEnricher) {
      note.content = workflowDetails.noteEnricher(
        patient,
        questionSet,
        workflowDetails.relevantPatientQuestionSections,
        { lastNote, lastNotesOther },
        patientDiagnosis,
        goalAndObjectiveForm,
      );
    }

    note.content.call_timer = formatAllottedTime(allottedTime);
    note.call_type = eventType;
    note.content.discharges = [];
    note.cp_credentials = provider?.cp_credentials ?? null;

    return note;
  };

  handleCallTypeChange = async (event_type) => {
    const data = {
      event_type: event_type,
    };
    const questionSet = await sharedRequests.get_patient_call_question_sets({ data });
    const workflow = _.cloneDeep(getCPTWorkflow(event_type, this.props.profile));
    const note = this.getEmptyNote(
      this.state.patient,
      this.state.lastNote,
      this.state.lastNotesOther,
      this.state.patientDiagnosis,
      event_type,
      this.props.profile,
      questionSet,
      workflow,
      // set the goal and objective progress to the current state so that it doesnt get reset
      // for BHS notes data is reset for other fields see https://aptihealth.atlassian.net/browse/AT1-6946
      // for the fix
      this.state?.note?.content?.goals_objectives_progress_v2,
    );

    this.setState({
      event_type,
      note,
      questionSet,
      workflow,
    });

    return { note, questionSet, workflow };
  };

  editHandler = (key, value, nested = true) => {
    this.setState((prevState) => {
      return {
        note: updateNote(prevState.note, nested, key, value),
      };
    });
  };

  resetValidationHandler = (noteErrorKey, noteErrorValue) => {
    const noteErrors = { ...this.state.noteErrors };

    noteErrors[noteErrorKey] = noteErrorValue;
    this.setState({ noteErrors });
  };

  saveNoteHandler = async (note) => {
    try {
      const urlParams = { patientId: this.state.patientId };

      return providerRequests.post_progress_note({ data: note, urlParams });
    } catch (e) {
      console.log(e);
    }
  };

  noteDraftSubmitHandler = async () => {
    if (!this.noteValidation(true)) {
      return;
    }

    let note = this.state.note;

    await this.saveNoteHandler(note);
    this.props.history.goBack(-1);
  };

  noteValidation = (isDraft = false) => {
    let note = this.state.note;
    const medications =
      note["content"] && note["content"]["medications"] ? note["content"]["medications"] : [];
    const noteErrors = {};

    if (!isDraft) {
      if (!note["ICD_10_axis_1_diagnosis"] || note["ICD_10_axis_1_diagnosis"].length === 0) {
        noteErrors["ICD_10_axis_1_diagnosis"] = "ICD-10 Primary Diagnosis is required";
      }
    }

    if (note["call_id"] === "") {
      noteErrors["call_id"] = "Call Linking is required";
    }

    if (note["session_to"] === "") {
      noteErrors["session_to"] = "Session To is required";
    }

    if (note["session_from"] === "") {
      noteErrors["session_from"] = "Session From is required";
    }

    if (this.props.profile.provider_type === PRESCRIBE && !note["service_location"]) {
      noteErrors["service_location"] = "Service Location is required";
    }

    if (medications.length > 0) {
      medications.forEach((medication) => {
        if (medication["medication_name"] === "") {
          noteErrors["medication_name"] = "Medication Name is required";
        }

        if (medication["dosage"] === "") {
          noteErrors["dosage"] = "Dosage is required";
        }
      });
    }

    if (checkInterventionAndPlanErrors(this.state.note?.content)) {
      // the error message here has no impact, the error is displayed by
      // formik
      noteErrors["goals_objectives_progress_v2"] = "goals_objectives_progress_v2_error";
    }

    this.setState({ noteErrors });

    return Object.keys(noteErrors).length === 0;
  };

  noteSubmitHandler = async () => {
    if (!this.noteValidation()) {
      return;
    }
    let note = this.state.note;

    note["signature"] = this.props.profile.name;
    note["completed_date_time"] = moment().format();
    note["note_state"] = "complete";
    await this.saveNoteHandler(note);
    await awaitNotesSummaryRefresh(this.state.patientId, this.props.dispatch);
    this.props.history.goBack(-1);
  };

  render() {
    const { note } = this.state;

    if (!note) {
      return <div />;
    }

    return (
      <EditNoteWrapper
        noteDraftSubmitHandler={this.noteDraftSubmitHandler}
        noteSubmitHandler={this.noteSubmitHandler}
        patientId={this.state.patientId}
      >
        <NotesLinkCall
          completedCalls={this.state.completedCalls}
          cptOptions={this.getCPTOptions()}
          editHandler={this.editHandler}
          errors={this.state.noteErrors}
          handleCallTypeChange={this.handleCallTypeChange}
          initialPatientQuestionSets={this.state.questionSet}
          lastNotesOther={this.state.lastNotesOther}
          note={note}
          patient={this.state.patient}
          resetValidationHandler={this.resetValidationHandler}
          workflow={this.state.workflow}
        />

        <GuidedSessionView
          editable
          editHandler={this.editHandler}
          errors={this.state.noteErrors}
          isCallManuallyLinked
          note={note}
          patientAnswersEditable
          views={{ createEditView: true, draftView: true }}
        />
      </EditNoteWrapper>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps)(withRouter(NewNoteV2));
